import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Download, X } from 'react-feather';
import './DocumentViewerModal.css';

const DocumentViewerModal = ({ 
  show, 
  onHide, 
  documentUrl, 
  documentName = 'Document',
  onDownload 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload(documentUrl);
    } else {
      // Fallback: open in new tab for download
      window.open(documentUrl, '_blank');
    }
  };

  // Determine file type from URL or name
  const getFileType = () => {
    const url = documentUrl?.toLowerCase() || '';
    const name = documentName?.toLowerCase() || '';
    
    if (url.includes('.pdf') || name.includes('.pdf')) {
      return 'pdf';
    }
    if (url.includes('.dwg') || name.includes('.dwg')) {
      return 'dwg';
    }
    if (url.includes('.doc') || name.includes('.doc')) {
      return 'doc';
    }
    if (url.includes('.docx') || name.includes('.docx')) {
      return 'docx';
    }
    return 'pdf'; // Default to PDF
  };

  const fileType = getFileType();

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="document-viewer-modal"
      dialogClassName="document-viewer-modal-dialog"
    >
      <Modal.Header className="document-viewer-modal-header">
        <Modal.Title className="d-flex align-items-center">
          <span className="me-2">Document Viewer</span>
          <span className="text-muted small ms-2">{documentName}</span>
        </Modal.Title>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleDownload}
            title="Download"
          >
            <Download size={16} />
            <span className="ms-1">Download</span>
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={onHide}
            title="Close"
          >
            <X size={16} />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body className="document-viewer-modal-body">
        {loading && (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading document...</p>
            </div>
          </div>
        )}
        
        {error && (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <p className="text-danger">Failed to load document.</p>
              <Button variant="outline-primary" onClick={() => window.open(documentUrl, '_blank')}>
                Open in New Tab
              </Button>
            </div>
          </div>
        )}

        {fileType === 'pdf' && (
          <iframe
            src={documentUrl}
            title={documentName}
            className="document-viewer-iframe"
            onLoad={handleLoad}
            onError={handleError}
            style={{ display: loading || error ? 'none' : 'block' }}
          />
        )}

        {(fileType === 'dwg' || fileType === 'doc' || fileType === 'docx') && (
          <div className="document-viewer-fallback">
            <div className="text-center p-5">
              <p className="mb-3">
                {fileType === 'dwg' 
                  ? 'DWG files cannot be previewed in the browser. Please download to view.'
                  : 'This document type cannot be previewed in the browser. Please download to view.'}
              </p>
              <Button variant="primary" onClick={handleDownload}>
                <Download size={16} className="me-2" />
                Download Document
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DocumentViewerModal;

